import * as React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import colors from 'components/utils/colors'

// --- Lib ---
import { parseShortcutKeys } from '@tiptap-ui/lib/tiptap-utils'

import {
  HEADING_SHORTCUT_KEYS,
  useHeading,
} from '@tiptap-ui/components/tiptap-ui/heading-button'

import { Button } from '@tiptap-ui/components/tiptap-ui-primitive/button'
import { Badge } from '@tiptap-ui/components/tiptap-ui-primitive/badge'
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'

const H1_H6_STYLES = {
  1: { fontSize: 40, lineHeight: 52, fontWeight: 300 },
  2: { fontSize: 28, lineHeight: 37, fontWeight: 300 },
  3: { fontSize: 20, lineHeight: 28, fontWeight: 400 },
  4: { fontSize: 16, lineHeight: 24, fontWeight: 400 },
  5: { fontSize: 14, lineHeight: 20, fontWeight: 500 },
  6: { fontSize: 12, lineHeight: 18, fontWeight: 500 },
}

const StyledButton = styled(Button)`
  &&& {
    height: auto;

    &[data-active-state="on"]:not([disabled]) {
      color: ${colors.contentSecondaryActive};
      background: ${colors.bgPrimarySelect};
    }

    &:hover:not([disabled]) {
      background: ${colors.bgPrimaryHover};
    }
  }
`

const StyledText = styled.span`
  &&& {
    font-family: Exo;
    font-size: ${props => H1_H6_STYLES[props.$level].fontSize + 'px'};
    font-weight: ${props => H1_H6_STYLES[props.$level].fontWeight};
    line-height: ${props => H1_H6_STYLES[props.$level].lineHeight + 'px'};
  }
`

export function HeadingShortcutBadge ({
  level,
  shortcutKeys = HEADING_SHORTCUT_KEYS[level],
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>
}

HeadingShortcutBadge.propTypes = {
  level: PropTypes.number.isRequired,
  shortcutKeys: PropTypes.string,
}

/**
 * Button component for toggling heading in a Tiptap editor.
 *
 * For custom button implementations, use the `useHeading` hook instead.
 */
export const HeadingButton = React.forwardRef((
  {
    editor: providedEditor,
    level,
    text,
    hideWhenUnavailable = false,
    onToggled,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    canToggle,
    isActive,
    handleToggle,
    label,
    shortcutKeys,
  } = useHeading({
    editor,
    level,
    hideWhenUnavailable,
    onToggled,
  })

  const handleClick = React.useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleToggle()
  }, [handleToggle, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <StyledButton
      type='button'
      data-style='ghost'
      data-active-state={isActive ? 'on' : 'off'}
      role='button'
      tabIndex={-1}
      disabled={!canToggle}
      data-disabled={!canToggle}
      aria-label={label}
      aria-pressed={isActive}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}
    >
      {children ?? (
        <>
          {text && <StyledText className='tiptap-button-text' $level={level}>{text}</StyledText>}
          {showShortcut && (
            <HeadingShortcutBadge level={level} shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </StyledButton>
  )
})

HeadingButton.displayName = 'HeadingButton'

HeadingButton.propTypes = {
  editor: PropTypes.object,
  level: PropTypes.number.isRequired,
  text: PropTypes.string,
  hideWhenUnavailable: PropTypes.bool,
  onToggled: PropTypes.func,
  showShortcut: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
}
